import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Mail, Lock, User, Check, Sparkles, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function FreeMembers() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    agreeToTerms: false
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.agreeToTerms) {
      toast.error("Please agree to the Terms of Service");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/api/members/auth/register", {
        email: formData.email,
        password: formData.password,
        name: formData.name
      });
      
      if (res.data.token) {
        localStorage.setItem("memberToken", res.data.token);
        localStorage.setItem("memberUser", JSON.stringify(res.data.user));
        toast.success("Welcome! Your free account has been created 🎉");
        navigate("/members/free");
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4" style={{ background: "#0a0a0a" }}>
      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }} />
      
      {/* Back to home link */}
      <a 
        href="/" 
        className="absolute top-6 left-6 z-10 flex items-center gap-2 text-sm text-zinc-400 hover:text-purple-400 transition">
        <ArrowLeft size={16} />
        Back to Home
      </a>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background: "rgba(34, 197, 94, 0.1)", border: "1px solid rgba(34, 197, 94, 0.3)" }}>
            <Sparkles size={16} style={{ color: "#22c55e" }} />
            <span className="text-sm font-semibold" style={{ color: "#22c55e" }}>FREE MEMBERSHIP</span>
          </div>
          <h1 className="text-4xl font-black mb-2" style={{ color: "#fff" }}>
            Sign Up Free
          </h1>
          <p className="text-zinc-400">
            No credit card required • Access free content instantly
          </p>
        </div>

        {/* Registration Form */}
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <User size={16} className="inline mr-2" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                required
                className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-green-500 transition"
                style={{
                  background: "rgba(0,0,0,0.3)",
                  borderColor: "rgba(113, 113, 122, 0.3)"
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Mail size={16} className="inline mr-2" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-green-500 transition"
                style={{
                  background: "rgba(0,0,0,0.3)",
                  borderColor: "rgba(113, 113, 122, 0.3)"
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Lock size={16} className="inline mr-2" />
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 8 characters"
                required
                className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-green-500 transition"
                style={{
                  background: "rgba(0,0,0,0.3)",
                  borderColor: "rgba(113, 113, 122, 0.3)"
                }}
              />
              <p className="text-xs text-zinc-500 mt-1">Minimum 8 characters</p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className="mt-1"
                style={{ accentColor: "#22c55e" }}
              />
              <label className="text-sm text-zinc-400">
                I agree to the <a href="/legal" className="text-green-400 hover:underline">Terms of Service</a> and <a href="/legal" className="text-green-400 hover:underline">Privacy Policy</a>. I confirm I am 18+ years old.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-bold text-lg transition hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                color: "#fff"
              }}>
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Create Free Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-zinc-400">
              Already have an account?{" "}
              <a href="/members/login" className="text-green-400 hover:underline font-medium">
                Sign In
              </a>
            </p>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-8 backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4">What's Included (Free):</h3>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
              Access to free preview content and galleries
            </li>
            <li className="flex items-start gap-2">
              <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
              Daily free posts and updates
            </li>
            <li className="flex items-start gap-2">
              <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
              Community access and member features
            </li>
            <li className="flex items-start gap-2">
              <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
              No credit card required
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
